import { Box, Fade, IconButton, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { tokens } from "../../../../theme";
import Shbutton from "../../../theme/buttons/Shbutton";
import SidepanelHeaderActiongroup from "../../../theme/sidepanel2/SidepanelHeaderActiongroup";
import Sidepanel2 from "../../../theme/sidepanel2/Sidepanel2";
import ShRowSkeleton from "../../../theme/skeleton/ShRowSkeleton";
import { spWebshopCredentialsDetailsConfig } from "./spWebshopCredentialsDetailsConfig";
import Details from "./Views/Details";
import Sync from "./Views/Sync";
import Orders from "./Views/Orders";
import ShippingOptions from "./Views/ShippingOptions";
import { useGetWebshopCredentialsSingleQuery, useEditWebshopCredentialsMutation, useSyncWebshopProductsMutation, useCreateWebshopCredentialsWithOAuthMutation } from "../../../../newapi/webshop/webshopSlice";
import { useGetSettingsQuery } from "../../../../newapi/global/generalApiSlice";
import { showToastMessageUpdateRequest, showToastMessageRequestError, showToastMessage, showToastMessageSyncProductsRequestError } from "../../../../utils/toasts";
import MConfirmationWithValue from "../../Modals/MConfirmationWithValue";
import MConfirmation from "../../Modals/MConfirmation";
import AMWebshopCredentialsSp from "../../Modals/ActionModal/AMWebshopCredentialsSp";
import Text from "../../../theme/text/Text";
import { ArrowDownward, ArrowDownwardOutlined, ArrowDropDown, ArrowForwardIos, MoveDownOutlined, StorefrontOutlined } from "@mui/icons-material";
import ShLabel from "../../../theme/label/ShLabel";
import { getWebshopLogoNoSize } from "../../../../utils/logomapper";
import ButtonGroup from "../../../theme/buttons/ButtonGroup";
import ShCopyButton from "../../../theme/ShCopy";
import MLoader from "../../Modals/MLoader";
import MDynamicValues from "../../Modals/MDynamicValues";
import { ENV_TYPES } from "../../Sidebar";

const SidePanelActions = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isRelation = selectedEnvironmentType === ENV_TYPES[200];

    return (
        <SidepanelHeaderActiongroup>
            <Shbutton isCancel={true} 
                onClick={() => props.setIsExtended(false)} 
                color="primary" 
                variant="text" 
                startIcon={<ArrowRightAltIcon/>}
            >
                Cancel
            </Shbutton>
            <ButtonGroup isSidepanel version={2}>
                {!isRelation &&
                <Shbutton
                    onClick={() => window.location.href = `/warehouse/webshopcredentials/data?webshop=${props.webshopCredentialsId}`}
                    endIcon={<ArrowForwardIos/>}
                    className={"group-btn"}
                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                    variant="contained"
                >
                    {t('overview')}
                </Shbutton>
                }
                <Shbutton
                    onClick={props.handleFilterPopoverClick}
                    endIcon={<ArrowDropDown/>}  
                    className={"group-btn"}
                    color={"primaryGrey"}
                    variant="contained"
                >
                    {t('actions')}
                </Shbutton>
            </ButtonGroup>
        </SidepanelHeaderActiongroup>
    )
}

const CHECKOUT_WEBSHOP_TYPES = ["woocommerce", "magento", "shopify"];

const SpWebshopCredentialsDetails = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const isFulfilment = useSelector(state => state.settings.environment.isFulfilment);

    const { data: webshopCredsData, isFetching, isLoading, isError } = useGetWebshopCredentialsSingleQuery(props.webshopCredentialsId, {
        skip: !props.webshopCredentialsId
    });

    const { data: shippingSettings } = useGetSettingsQuery("shipping");
    const shippingOptionsEnabled = shippingSettings?.find(s => s.key === "shipping_options_enabled")?.value === "true";

    const config = useMemo(() => {
        const webshopType = webshopCredsData?.webshop_type || webshopCredsData?.webshopType;
        const typeSupportsShipping = webshopType && CHECKOUT_WEBSHOP_TYPES.includes(webshopType);
        // Only show shippingOptions when the global setting is on AND the webshop type supports it
        if (shippingOptionsEnabled && typeSupportsShipping) {
            return spWebshopCredentialsDetailsConfig;
        }
        // Exclude shippingOptions tab when disabled or unsupported webshop type
        const { shippingOptions, ...rest } = spWebshopCredentialsDetailsConfig;
        return rest;
    }, [webshopCredsData?.webshop_type, webshopCredsData?.webshopType, shippingOptionsEnabled]);

    // console.log("props.webshopCredentialsId",props.webshopCredentialsId)
    // console.log("webshopCredsData",webshopCredsData)
    const [editWebshopCredentials, {isLoading: isEditLoading}] = useEditWebshopCredentialsMutation();
    const [syncWebshopProducts] = useSyncWebshopProductsMutation();
    const [isSyncing, setIsSyncing] = useState(false);

    // Actions dropdown state
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const filterPopoverOpen = Boolean(filterAnchorEl);
    const filterPopoverId = filterPopoverOpen ? 'simple-popover' : undefined;

    // Confirmation modal state
    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [confirmationAction, setConfirmationAction] = useState(null);
    const [openReauthConfirmation, setOpenReauthConfirmation] = useState(false);

    const handleFilterPopoverClick = (event) => {
        setFilterAnchorEl(event.currentTarget);
    };

    const handleFilterPopoverClose = () => {
        setFilterAnchorEl(null);
    };

    const handleSyncClick = async (fromConfirm = false) => {
        if (!webshopCredsData) return;
        
        if (isFulfilment && !fromConfirm) {
            setConfirmationAction('sync');
            setOpenConfirmation(true);
        } else {
            // Direct sync for non-fulfilment
            setIsSyncing(true);
            try {
                const response = await syncWebshopProducts({ id: webshopCredsData.id }).unwrap();
                showToastMessageUpdateRequest(t, response);
            } catch (err) {
                showToastMessageSyncProductsRequestError(t, err);
            } finally {
                setIsSyncing(false);
            }
        }
    };

    const onActivate = async () => {
        try {
            // Determine which field to update based on what exists in the data
            const updateData = { ...webshopCredsData };
            
            if ('active' in webshopCredsData) {
                updateData.active = true;
            } else if ('state' in webshopCredsData) {
                updateData.state = 'active';
            }
            
            const response = await editWebshopCredentials({
                id: webshopCredsData.id,
                state: updateData.state,
            }).unwrap();
            
            showToastMessageUpdateRequest(t, response);
        } catch (error) {
            console.error('Error activating webshop:', error);
            showToastMessageRequestError(t('error_activating_webshop'), t, error);
        }
    };

    const onDeactivate = async () => {
        try {
            // Determine which field to update based on what exists in the data
            const updateData = { ...webshopCredsData };
            
            if ('active' in webshopCredsData) {
                updateData.active = false;
            } else if ('state' in webshopCredsData) {
                updateData.state = 'inactive';
            }
            
            const response = await editWebshopCredentials({
                id: webshopCredsData.id,
                ...updateData
            }).unwrap();
            
            showToastMessageUpdateRequest(t, response);
        } catch (error) {
            console.error('Error deactivating webshop:', error);
            showToastMessageRequestError(t('error_deactivating_webshop'), t, error);
        }
    };

    const confirmAction = async () => {
        if (!webshopCredsData) return;

        if (confirmationAction === 'sync') {
            setOpenConfirmation(false);
            setConfirmationAction(null);
            await handleSyncClick(true);
            return;
        }

        try {
            if (confirmationAction === 'activate') {
                await onActivate();
            } else if (confirmationAction === 'deactivate') {
                await onDeactivate();
            }
        } catch (err) {
            const errorMessage = confirmationAction === 'sync' ? t('sync_failed') : 
                                confirmationAction === 'activate' ? t('activation_failed') : 
                                t('deactivation_failed');
            showToastMessageRequestError(errorMessage, t, err);
        } finally {
            setOpenConfirmation(false);
            setConfirmationAction(null);
        }
    };

    const getConfirmationData = () => {
        switch (confirmationAction) {
            case 'sync':
                return {
                    title: t('confirm_sync_title'),
                    subtitle: t('confirm_sync_description'),
                    content: <Text>{t('confirm_sync_content')}</Text>
                };
            case 'activate':
                return {
                    title: t('confirm_activate_title'),
                    subtitle: t('confirm_activate_description'),
                    content: <Text>{t('confirm_activate_content')}</Text>
                };
            case 'deactivate':
                return {
                    title: t('confirm_deactivate_title'),
                    subtitle: t('confirm_deactivate_description'),
                    content: <Text>{t('confirm_deactivate_content')}</Text>
                };
            default:
                return {
                    title: '',
                    subtitle: '',
                    content: <Text></Text>
                };
        }
    };

    const defaultIcon = useMemo(() => ({
        theme: theme.palette.mode === "dark" ? "blue" : "primary",
        content: <StorefrontOutlined/>
    }), [theme.palette.mode]);

    const defaultOptions = useMemo(() => [
        { component: <ShRowSkeleton heading={t('name')} /> },
        { component: <ShRowSkeleton heading={t('status')} /> },
        { component: <ShRowSkeleton heading={t('type')} /> },
        { component: <ShRowSkeleton heading={t('token')} /> }
    ], [t]);

    const setIsExtendedCallback = useCallback(() => props.setIsExtended(false), [props.setIsExtended]);

    const defaultState = useMemo(() => ({
        title: "",
        subTitle: "",
        icon: defaultIcon,
        options: defaultOptions,
        actions: <SidePanelActions 
            setIsExtended={setIsExtendedCallback} 
            onSync={handleSyncClick}
            handleFilterPopoverClick={handleFilterPopoverClick}
            webshopCredentialsId={props.webshopCredentialsId}
        />
    }), [defaultIcon, defaultOptions, setIsExtendedCallback]);

    const [panel, setPanel] = useState(defaultState);

    useEffect(() => {
        setPanel(defaultState);
    }, [isFetching, props.webshopCredentialsId, theme.palette.mode]);

    const [createWebshopCredentialsWithOAuth] = useCreateWebshopCredentialsWithOAuthMutation();

    const performOAuth = () => {
        createWebshopCredentialsWithOAuth(webshopCredsData).then((res) => {
            if (res.data?.success) {
                window.location.href = res.data?.url;
            }
        });
    }

    const [updateSyncOpen, setUpdateSyncOpen] = useState(false);
    const handleUpdateSync = async (values) => {
        try {
            if (isEditLoading) return;

            const updateObj = {
                id: props.webshopCredentialsId,
                lastOrderSync: values.syncDate
            }

            const syncDate = new Date(values.syncDate);

            if (syncDate > new Date()) {
                showToastMessage("error", t("date_cannot_be_in_the_future"));
                return;
            }
            const minDate = new Date();
minDate.setHours(0, 0, 0, 0);
minDate.setDate(minDate.getDate() - 4);

if (
    webshopCredsData.webshop_type === "bol" &&
    syncDate < minDate
) {
    showToastMessage("error", t("bol_max_4_days_in_past"));
    return;
}


            const response = await editWebshopCredentials(updateObj).unwrap();
            showToastMessageUpdateRequest(t, response);
        } catch (err) {
            showToastMessageUpdateRequest(t, {success: false, error: err.error});
        }
    };

    const handleOAuth = () => {
        if (webshopCredsData?.testConnection) {
            setOpenReauthConfirmation(true);
        } else {
            performOAuth();
        }
    }

    const getTokenElement = (token) => {
        return (<Box sx={{
                        minWidth:"100%",
                        height:"100%",
                        display:"flex",
                        alignItems:"center",
                        position:"relative",
                        whiteSpace:"nowrap",
                        wordBreak:"keep-all",
                        textOverflow:"ellipsis",
                        overflow:"hidden",
                    }}>
                        <Text noBreak>{`${token && token.length > 24 ? `${token.substring(0,21)}...` : (token ?? "")}`}</Text>
                        <ShCopyButton 
                            styles={{button:{
                                background:theme.palette.mode === "dark" ? colors.grey[600] :  colors.grey[200],
                                color: colors.txt["primary"],
                                ml:1
                            }}} 
                            className={"copyel"} 
                            value={token}
                        ></ShCopyButton>
                    </Box>)
    }

    useEffect(() => {
        if (!isLoading && !isFetching && webshopCredsData) {
            // Check if the webshop is active - handle both 'active' boolean and 'state' string
            const isActive = webshopCredsData.active === true || 
                             webshopCredsData.active === 'true' ||
                             webshopCredsData.state === 'active';



            setPanel(prevPanel => {
                // Get the webshop-specific logo
                const webshopIcon = {
                    //theme: theme.palette.mode === "dark" ? "blue" : "primary",
                    content: getWebshopLogoNoSize(webshopCredsData.webshop_type || webshopCredsData.webshopType,webshopCredsData.description)
                };
                    
                return {
                    ...prevPanel,
                    beforeChild: (
                        <Box
                            sx={{
                                mb:3,
                                "@media screen and (max-width: 63.9375em)" : {
                                    display:"none",
                                }
                            }}
                        >
                            {console.log(webshopCredsData)}
                            {((webshopCredsData.webshop_type === "shopify" || webshopCredsData.webshop_type === "bol") && !webshopCredsData.testConnection) ?
                                <ShLabel
                                    size={32}
                                    palette="normal"
                                    variant={!webshopCredsData.testConnection ? "red" : "green"}
                                    onClick={!webshopCredsData.testConnection ? () => handleOAuth() : undefined}
                                    clickable={!webshopCredsData.testConnection}
                                >
                                    {!webshopCredsData.testConnection ? t("connection_failed_click") : t("connection_active")}
                                </ShLabel>
                                :
                                <ShLabel size={32} palette="normal"  variant={!webshopCredsData.testConnection ? "red" : "green"}>
                                    {!webshopCredsData.testConnection ? t("connection_failed") : t("connection_active")}
                                </ShLabel>
                            }
                        </Box>
                    ),
                    afterChild: (
                        <Box
                            sx={{
                                mt:3,
                                "@media screen and (min-width: 63.9375em)" : {
                                    display:"none",
                                }
                            }}
                        >
                            {((webshopCredsData.webshop_type === "shopify" || webshopCredsData.webshop_type === "bol") && !webshopCredsData.testConnection) ?
                                <ShLabel
                                    size={32}
                                    palette="normal"
                                    variant={!webshopCredsData.testConnection ? "red" : "green"}
                                    onClick={!webshopCredsData.testConnection ? () => handleOAuth() : undefined}
                                    clickable={!webshopCredsData.testConnection}
                                >
                                    {!webshopCredsData.testConnection ? t("connection_failed_click") : t("connection_active")}
                                </ShLabel>
                                :
                                <ShLabel size={32} palette="normal"  variant={!webshopCredsData.testConnection ? "red" : "green"}>
                                    {!webshopCredsData.testConnection ? t("connection_failed") : t("connection_active")}
                                </ShLabel>
                            }
                        </Box>
                    ),
                    title: `${webshopCredsData.description}`,
                    subTitle: [webshopCredsData.relation?.name],
                    icon: webshopIcon,
                    options: [
                        { heading: t('name'), content: webshopCredsData.name || webshopCredsData.description },
                        { heading: t('status'), content: isActive ? t('active') : t('inactive') },
                        { heading: t('type'), content: webshopCredsData.webshopType || webshopCredsData.webshop_type },
                        // Hide token for Bol SSO
                        ...((webshopCredsData.webshop_type === 'bol' && webshopCredsData.credentials?.sso) ? [] : [
                            { heading: t('token'), content: getTokenElement(webshopCredsData.token) },
                        ]),
                    ],
                    actions: <SidePanelActions 
                        setIsExtended={setIsExtendedCallback} 
                        onSync={handleSyncClick}
                        handleFilterPopoverClick={handleFilterPopoverClick}
                        webshopCredentialsId={props.webshopCredentialsId}
                    />
                }
            });
        }
    }, [webshopCredsData, isFetching, isLoading, t]);

    return (
        <>
            <Sidepanel2
                skeleton={isLoading || isFetching}
                tabConfig={config}
                content={panel} 
                isExtended={props.isExtended} 
                setIsExtended={props.setIsExtended} 
            >
                <>
                    <Details config={config.details} data={webshopCredsData} setIsExtended={setIsExtendedCallback} />
                    <Sync config={config.sync} data={webshopCredsData} />
                    <Orders config={config.spOrders} webshopCredentialsId={props.webshopCredentialsId}/>
                    {config.shippingOptions && (
                        <ShippingOptions config={config.shippingOptions} webshopCredentialsId={props.webshopCredentialsId} webshopType={webshopCredsData.webshop_type || webshopCredsData.webshopType} />
                    )}
                </>
            </Sidepanel2>
            
            <AMWebshopCredentialsSp
                id={filterPopoverId}
                open={filterPopoverOpen}
                anchorEl={filterAnchorEl}
                onClose={handleFilterPopoverClose}
                webshopCredsData={webshopCredsData}
                onActivate={onActivate}
                onDeactivate={onDeactivate}
                onSync={handleSyncClick}
                onOAuth={handleOAuth}
                onSyncOpen={setUpdateSyncOpen}
                isFulfilment={isFulfilment}
            />

          <MLoader
            open={isSyncing}
            message={t('synchronizing_it_can_take_a_while')}
          />
            
            <MConfirmationWithValue
                open={openConfirmation}
                handleClose={() => setOpenConfirmation(false)}
                confirm={confirmAction}
                data={getConfirmationData()}
            />

            <MConfirmation
                open={openReauthConfirmation}
                handleClose={() => setOpenReauthConfirmation(false)}
                confirm={performOAuth}
                title={t('confirm_reauth_title')}
                subtitle={t('confirm_reauth_description')}
                content={t('confirm_reauth_content')}
            />
                    <MDynamicValues
                        open={updateSyncOpen}
                        handleClose={() => setUpdateSyncOpen(false)}
                        title={t("update_order_sync")}
                        labelWidth={144}
                        fields={[
                            { name: "syncDate", label: t("date"), type: "date" },
                        ]}
                        onConfirm={(values) => handleUpdateSync(values)}
                    />
        </>
    );
};

export default SpWebshopCredentialsDetails;
